import { cn } from "@/lib/utils"
import { Check } from "lucide-react"
import type React from "react"

/** Generate-screen primitives shared by the scope picker.
 *
 *  Extracted when conformity v1 was retired (MC_CUSTOMER_STUDIO_CONFORMITY_V2
 *  removed): v2 is the only Generate screen, and these were the only parts of
 *  the old module it still used. */

export function Step({
  n,
  done,
  heading,
  children,
}: {
  n: number
  done: boolean
  heading: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2.5">
        <span
          className={cn(
            "font-mono flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-semibold",
            done
              ? "border-yellow1 bg-yellow1 text-black"
              : "border-grey4 bg-grey2 text-grey5",
          )}
        >
          {n}
        </span>
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-grey5">
          {heading}
        </h2>
      </div>
      {children}
    </div>
  )
}

export function ScopeTile({
  active,
  disabled,
  icon,
  title,
  subtitle,
  badge,
  onClick,
}: {
  active: boolean
  disabled?: boolean
  icon: React.ReactNode
  title: string
  subtitle: string
  badge?: string
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        "flex items-start gap-3 rounded-md border bg-grey2 p-3.5 text-left transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-yellow1/50",
        active
          ? "border-yellow1 bg-yellow1/4"
          : "border-grey4 hover:border-grey5",
        disabled && "cursor-not-allowed opacity-55 hover:border-grey4",
        !disabled && !active && "cursor-pointer",
      )}
    >
      <span
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
          active
            ? "bg-yellow1 text-black"
            : "border border-grey4 bg-grey1 text-grey5",
        )}
      >
        {icon}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="text-[13px] font-semibold text-white">{title}</h3>
          {badge && (
            <span className="ml-auto rounded-full border border-grey4 bg-grey1 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-grey5">
              {badge}
            </span>
          )}
        </div>
        <p className="mt-1 text-[11.5px] leading-normal text-grey5">
          {subtitle}
        </p>
      </div>

      <span
        className={cn(
          "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
          active ? "border-yellow1" : "border-grey5",
        )}
      >
        {active && <Check size={10} className="text-yellow1" strokeWidth={3} />}
      </span>
    </button>
  )
}

export function flipMember(prev: Set<string>, id: string): Set<string> {
  const next = new Set(prev)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return next
}
